import { inject, Injectable } from '@angular/core';
import type { Motion, TimelineSegment, TrimSelection } from '@ecam/eventq-components/playback';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AgentDataService } from '../../../core/services/agent-data.service';
import { StreamingService } from '../../../core/services/streaming.service';
import type { EventAiTagVariant, EventCameraItem } from '../../../core/models/entities/event-detail.model';
import type {
  DetectionBoundingBox,
  EventPlaybackClipRequest,
  TimelineSegmentDto,
} from '../../../core/models/entities/event-playback.model';
import {
  filterBoxesByEventTypes,
  generateTimedDetectionSequence,
  normalizeDetectionBoxes,
  pickDetectionForAlarm,
  syncDetectionOffsetsForWindow,
} from '../utils/detection-bbox.util';
import { MOCK_ARCHIVE_SEGMENT_KEY } from '../utils/expand-mock-playback-segments';
import { resolveMediaAssetUrl } from '../utils/media-url.util';
import {
  SAMPLE_PLAYBACK_DURATION_SECONDS,
  SAMPLE_PLAYBACK_MEDIA_END,
  SAMPLE_PLAYBACK_MEDIA_START,
  SAMPLE_PLAYBACK_MEDIA_URL,
} from '../utils/sample-playback-media';

export type FetchLiveSegmentsOptions = {
  playbackWindow?: { start: Date; end: Date } | null;
  /**
   * True UTC instant of the footage. Orchid `start` is Unix epoch milliseconds,
   * so this value is sent unchanged for every NVR zone.
   */
  startMs?: number;
  /** Historical fullscreen rate. Omitted at 1× and for live playback. */
  playbackRate?: number;
};

/** Mirrors @ecam/eventq-components/playback Camera (not exported from package). */
export interface PlaybackCamera {
  selected: boolean;
  id: number;
  segments: Record<string, TimelineSegment[]>;
  name: string;
  isPlaying: boolean;
}

/** Figma Fullscreen Playback (5989:46362) — orange/blue/green/red 300 event bars. */
export const PLAYBACK_MOTION_OPTIONS: Motion[] = [
  { value: 'motion', label: 'Motion', color: '#ffb74d' },
  { value: 'human', label: 'Human', color: '#64b5f6' },
  { value: 'vehicle', label: 'Vehicle', color: '#81c784' },
  { value: 'gun', label: 'Gun', color: '#e57373' },
];

export {
  SAMPLE_PLAYBACK_DURATION_SECONDS,
  SAMPLE_PLAYBACK_MEDIA_END,
  SAMPLE_PLAYBACK_MEDIA_START,
  SAMPLE_PLAYBACK_MEDIA_URL,
};

const SAMPLE_VIDEO_PATH = SAMPLE_PLAYBACK_MEDIA_URL;

export interface SegmentRemapWindow {
  start: Date;
  end: Date;
}

export interface AvailableMediaWindow {
  start: Date;
  end: Date;
  durationSeconds: number;
}

/** Union of all segment bounds for the selected/available cameras. */
export function resolveAvailableMediaWindow(
  cameras: PlaybackCamera[],
): AvailableMediaWindow {
  let minStart = Number.POSITIVE_INFINITY;
  let maxEnd = Number.NEGATIVE_INFINITY;

  for (const camera of cameras) {
    for (const list of Object.values(camera.segments)) {
      for (const segment of list ?? []) {
        const startMs = new Date(segment.start).getTime();
        const endMs = new Date(segment.end).getTime();
        if (Number.isFinite(startMs)) {
          minStart = Math.min(minStart, startMs);
        }
        if (Number.isFinite(endMs)) {
          maxEnd = Math.max(maxEnd, endMs);
        }
      }
    }
  }

  if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd) || maxEnd <= minStart) {
    const start = new Date(SAMPLE_PLAYBACK_MEDIA_START);
    const end = new Date(SAMPLE_PLAYBACK_MEDIA_END);
    return {
      start,
      end,
      durationSeconds: SAMPLE_PLAYBACK_DURATION_SECONDS,
    };
  }

  return {
    start: new Date(minStart),
    end: new Date(maxEnd),
    durationSeconds: (maxEnd - minStart) / 1000,
  };
}

/**
 * Caliber sizes the timeline as `minutes * pixelsPerMinute`. Short clips (~10s)
 * collapse to a few pixels at the default 20 ppm — scale up so the window fills
 * the viewport and event bars are visible.
 */
export function computeLibraryPixelsPerMinute(
  durationSeconds: number,
  viewportWidthPx: number,
): number {
  const minutes = Math.max(durationSeconds / 60, 1 / 60);
  const targetWidth = Math.max(viewportWidthPx, 320);
  return Math.max(20, targetWidth / minutes);
}

/**
 * Shift every segment so the media cluster ends at `windowEnd` (ticker / anchor).
 * Mock fixtures use absolute times that otherwise fall far outside a live 24h canvas.
 * Relative gaps between AI-tag events are preserved.
 */
export function shiftSegmentsToWindowEnd(
  cameras: PlaybackCamera[],
  windowEnd: Date,
): PlaybackCamera[] {
  const media = resolveAvailableMediaWindow(cameras);
  const shiftMs = windowEnd.getTime() - media.end.getTime();
  if (!Number.isFinite(shiftMs) || shiftMs === 0) {
    return cameras;
  }

  return cameras.map((camera) => {
    const segments: Record<string, TimelineSegment[]> = {};
    for (const [eventType, list] of Object.entries(camera.segments)) {
      if (!list?.length) {
        continue;
      }
      segments[eventType] = list.map((segment) => ({
        ...segment,
        start: new Date(new Date(segment.start).getTime() + shiftMs),
        end: new Date(new Date(segment.end).getTime() + shiftMs),
      }));
    }
    return { ...camera, segments };
  });
}

/** Canonical sample-media window used when the library timeline owns the range. */
export function samplePlaybackMediaWindow(): AvailableMediaWindow {
  return {
    start: new Date(SAMPLE_PLAYBACK_MEDIA_START),
    end: new Date(SAMPLE_PLAYBACK_MEDIA_END),
    durationSeconds: SAMPLE_PLAYBACK_DURATION_SECONDS,
  };
}

export function resolvePlaybackMediaUrl(url: string | undefined | null): string {
  const candidate = url?.trim() || SAMPLE_VIDEO_PATH;
  return resolveMediaAssetUrl(candidate) || SAMPLE_VIDEO_PATH;
}

@Injectable({ providedIn: 'root' })
export class EventPlaybackService {
  private readonly agentData = inject(AgentDataService);
  private readonly streaming = inject(StreamingService);
  private readonly liveRequestSequences = new WeakMap<
    BehaviorSubject<PlaybackCamera[]>,
    number
  >();

  sendCameraPtz(numericId: number, body: Parameters<AgentDataService['sendCameraPtz']>[1]) {
    return this.agentData.sendCameraPtz(numericId, body);
  }

  loadHistoricPlaybackAlarms(
    siteId: number,
    cameraNumericId: number,
    extraEventIds: readonly string[] = [],
  ) {
    return this.agentData.getHistoricPlaybackAlarms(siteId, cameraNumericId, extraEventIds);
  }

  submitCameraReport(
    eventId: string,
    cameraId: string,
    body: Parameters<AgentDataService['submitCameraReport']>[2],
    site?: Parameters<AgentDataService['submitCameraReport']>[3],
  ) {
    return this.agentData.submitCameraReport(eventId, cameraId, body, site);
  }

  captureSnapshot(eventId: string, body: Parameters<AgentDataService['captureSnapshot']>[1]) {
    return this.agentData.captureSnapshot(eventId, body);
  }

  attachAlarmClip(eventId: string, body: Parameters<AgentDataService['attachAlarmClip']>[1]) {
    return this.agentData.attachAlarmClip(eventId, body);
  }

  submitAiFeedback(
    eventId: string,
    detectionId: string,
    body: Parameters<AgentDataService['submitAiFeedback']>[2],
  ) {
    return this.agentData.submitAiFeedback(eventId, detectionId, body);
  }

  toPlaybackCamera(item: EventCameraItem, selected = false): PlaybackCamera {
    return {
      id: item.numericId,
      name: item.name,
      selected,
      isPlaying: false,
      segments: {},
    };
  }

  buildCameraList(items: EventCameraItem[], selectedCameraId: string | null): PlaybackCamera[] {
    return items.map((item) => this.toPlaybackCamera(item, item.id === selectedCameraId));
  }

  resolveCameraById(items: EventCameraItem[], cameraId: string | null): EventCameraItem | null {
    if (!cameraId) {
      return null;
    }
    const raw = cameraId.trim();
    if (!raw) {
      return null;
    }
    const stripped = raw.replace(/^cam-/i, '');
    return (
      items.find(
        (item) =>
          item.id === raw ||
          item.id === stripped ||
          String(item.numericId) === raw ||
          String(item.numericId) === stripped,
      ) ?? null
    );
  }

  resolveCameraByName(items: EventCameraItem[], cameraName: string): EventCameraItem | null {
    const normalized = cameraName.trim().toLowerCase();
    return (
      items.find(
        (item) =>
          item.name.toLowerCase() === normalized ||
          item.name.toLowerCase().startsWith(`${normalized} `),
      ) ?? null
    );
  }

  alarmMatchesCamera(
    alarm: { cameraId?: string; camera: string },
    cameraId: string | null,
    cameras: EventCameraItem[],
  ): boolean {
    if (!cameraId) {
      return true;
    }
    if (alarm.cameraId) {
      return alarm.cameraId === cameraId;
    }
    const camera = this.resolveCameraById(cameras, cameraId);
    if (!camera) {
      return false;
    }
    return this.resolveCameraByName([camera], alarm.camera) !== null;
  }

  async fetchLiveSegmentsAndUpdate(
    camera: EventCameraItem,
    currentCameras: PlaybackCamera[],
    update$: BehaviorSubject<PlaybackCamera[]>,
    streamType: 'main' | 'sub',
    options: FetchLiveSegmentsOptions = {},
  ): Promise<PlaybackCamera[]> {
    const streamUrl = camera.streamUrls?.[streamType];
    if (!streamUrl) {
      return currentCameras;
    }

    const requestSequence = (this.liveRequestSequences.get(update$) ?? 0) + 1;
    this.liveRequestSequences.set(update$, requestSequence);

    const playbackWindow = options.playbackWindow ?? null;
    const linkStartMs =
      typeof options.startMs === 'number' && Number.isFinite(options.startMs)
        ? Math.trunc(options.startMs)
        : undefined;
    const webrtcUrl = options.playbackRate
      ? await this.streaming.resolveWebrtcUrl(
          streamUrl,
          linkStartMs,
          camera.siteId,
          options.playbackRate,
        )
      : await this.streaming.resolveWebrtcUrl(streamUrl, linkStartMs, camera.siteId);
    if (
      !webrtcUrl ||
      this.liveRequestSequences.get(update$) !== requestSequence
    ) {
      return currentCameras;
    }

    const now = new Date();
    const segmentStart = playbackWindow?.start ?? now;
    const segmentEnd = new Date(segmentStart.getTime() + 24 * 60 * 60 * 1000);
    const synthetic = {
      numericId: camera.numericId,
      segments: {
        [MOCK_ARCHIVE_SEGMENT_KEY]: [
          {
            start: segmentStart.toISOString(),
            end: segmentEnd.toISOString(),
            url: webrtcUrl,
            cameraId: String(camera.numericId),
            type: streamType === 'sub' ? ('sub' as const) : ('main' as const),
          },
        ],
      },
    };

    const merged = this.mergeSegmentResponse(currentCameras, [synthetic], {
      start: segmentStart,
      end: segmentEnd,
    });
    update$.next(merged);
    return merged;
  }

  pickFirstSegment(camera: PlaybackCamera): TimelineSegment | null {
    return this.pickSegmentForCamera(camera);
  }

  pickSegmentForCamera(
    camera: PlaybackCamera,
    preferredTypes: string[] = [],
  ): TimelineSegment | null {
    for (const type of preferredTypes) {
      const preferred = camera.segments[type];
      if (preferred?.length) {
        return preferred[0];
      }
    }

    // Continuous mock recording lives under `archive` (not painted on the timeline).
    const archive = camera.segments['archive'];
    if (archive?.length) {
      return archive[0];
    }

    for (const segments of Object.values(camera.segments)) {
      if (segments?.length) {
        return segments[0];
      }
    }
    return null;
  }

  mergeSegmentResponse(
    currentCameras: PlaybackCamera[],
    segmentCameras: Array<{
      numericId: number;
      segments: Partial<Record<string, TimelineSegmentDto[]>>;
    }>,
    remapWindow?: SegmentRemapWindow,
  ): PlaybackCamera[] {
    const byId = new Map((segmentCameras ?? []).map((entry) => [entry.numericId, entry.segments]));

    return currentCameras.map((camera) => {
      const dtoSegments = byId.get(camera.id);
      if (!dtoSegments) {
        return camera;
      }

      const segments: Record<string, TimelineSegment[]> = {};
      for (const [eventType, list] of Object.entries(dtoSegments)) {
        if (!list?.length) {
          continue;
        }
        segments[eventType] = list.map((segment) => ({
          start: remapWindow?.start ?? new Date(segment.start),
          end: remapWindow?.end ?? new Date(segment.end),
          url: resolvePlaybackMediaUrl(segment.url),
          cameraId: segment.cameraId,
          type: segment.type,
        }));
      }

      return { ...camera, segments };
    });
  }

  /**
   * Requests a clipped export from the API and triggers a browser file download.
   */
  async downloadClip(eventId: string, selection: TrimSelection): Promise<string> {
    const body = toPlaybackClipRequest(selection);
    const { blob, filename } = await firstValueFrom(this.agentData.exportPlaybackClip(eventId, body));
    triggerBlobDownload(blob, filename);
    return filename;
  }

  async loadDetections(
    numericId: number,
    enabledAiEvents: EventAiTagVariant[],
    visibleTypes: EventAiTagVariant[],
    playbackWindow?: { start: Date; durationSeconds: number },
    selectedAlarmId?: string | null,
  ): Promise<DetectionBoundingBox[]> {
    try {
      const response = await firstValueFrom(this.agentData.getCameraDetections(numericId));
      let raw =
        response.boxes.length > 0 && response.boxes.some((box) => box.id)
          ? normalizeDetectionBoxes(response.boxes)
          : generateTimedDetectionSequence(numericId, 120, enabledAiEvents);

      if (playbackWindow && raw.some((box) => box.timestamp)) {
        let synced = syncDetectionOffsetsForWindow(
          raw,
          playbackWindow.start,
          playbackWindow.durationSeconds,
        );

        if (synced.length === 0 && selectedAlarmId) {
          synced = pickDetectionForAlarm(
            raw,
            selectedAlarmId,
            playbackWindow.start,
            playbackWindow.durationSeconds,
          );
        }

        raw = synced.length > 0 ? synced : raw;
      }

      return filterBoxesByEventTypes(raw, visibleTypes);
    } catch {
      return filterBoxesByEventTypes(
        generateTimedDetectionSequence(numericId, 120, enabledAiEvents),
        visibleTypes,
      );
    }
  }
}

export function toPlaybackClipRequest(selection: TrimSelection): EventPlaybackClipRequest {
  const cameraId =
    typeof selection.camera?.id === 'number'
      ? selection.camera.id
      : Number(selection.segment?.cameraId);
  return {
    cameraNumericId: Number.isFinite(cameraId) ? cameraId : 0,
    segmentUrl: selection.segment?.url ?? SAMPLE_VIDEO_PATH,
    trimStart: toIso(selection.trimStart),
    trimEnd: toIso(selection.trimEnd),
    eventType: selection.eventType || 'clip',
  };
}

/** Triggers a browser download for a blob (exported for tests). */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function toIso(value: Date | string): string {
  if (value instanceof Date) {
    return value.toISOString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toISOString();
}
